import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

import { apiPost } from "../api";
import { getTelegramWebApp } from "../telegramWebApp";

const AuthContext = createContext(null);

const TOKEN_STORAGE_KEY = "access_token";
const TELEGRAM_INIT_DATA_STORAGE_KEY = "telegram_init_data";
const AUTH_BYPASS_PARAM = "auth_bypass";
const AUTH_BYPASS_TOKEN = "dev-auth-bypass";
const TELEGRAM_LAUNCH_PARAM_NAMES = [
    "tgWebAppPlatform",
    "tgWebAppVersion",
    "tgWebAppData",
    "tgWebAppThemeParams",
    "tgWebAppStartParam",
];

function getStoredToken() {
    return localStorage.getItem(TOKEN_STORAGE_KEY) || "";
}

function isAuthBypassEnabled() {
    if (process.env.NODE_ENV !== "development") {
        return false;
    }

    return new URLSearchParams(window.location.search).get(AUTH_BYPASS_PARAM) === "1";
}

function getStoredTelegramInitData() {
    try {
        return sessionStorage.getItem(TELEGRAM_INIT_DATA_STORAGE_KEY)?.trim() || "";
    } catch {
        return "";
    }
}

function getTelegramLaunchParams() {
    const searchParams = new URLSearchParams(window.location.search);
    const hash = window.location.hash.startsWith("#")
        ? window.location.hash.slice(1)
        : window.location.hash;
    const hashParams = new URLSearchParams(hash);

    return {
        searchParams,
        hashParams,
    };
}

function getTelegramLaunchParam(name) {
    const { searchParams, hashParams } = getTelegramLaunchParams();
    return (hashParams.get(name) || searchParams.get(name) || "").trim();
}

function getTelegramInitData() {
    return getTelegramLaunchParam("tgWebAppData") || getStoredTelegramInitData();
}

function storeTelegramInitData(initData) {
    if (!initData) {
        return;
    }

    try {
        sessionStorage.setItem(TELEGRAM_INIT_DATA_STORAGE_KEY, initData);
    } catch {
        // Authorization still works for the initial launch even if storage is unavailable.
    }
}

function isTelegramSession() {
    const initData = getTelegramInitData();

    if (initData) {
        return true;
    }

    const { searchParams, hashParams } = getTelegramLaunchParams();
    const hasTelegramSearchParams = TELEGRAM_LAUNCH_PARAM_NAMES.some((name) => searchParams.has(name));
    const hasTelegramHashParams = TELEGRAM_LAUNCH_PARAM_NAMES.some((name) => hashParams.has(name));

    return hasTelegramSearchParams || hasTelegramHashParams;
}

export function AuthProvider({ children }) {
    const authBypassEnabled = isAuthBypassEnabled();
    const [token, setToken] = useState(() => authBypassEnabled ? AUTH_BYPASS_TOKEN : getStoredToken());
    const [telegramInitData, setTelegramInitData] = useState(() => authBypassEnabled ? "" : getTelegramInitData());
    const [isTelegram, setIsTelegram] = useState(() => authBypassEnabled ? false : isTelegramSession());
    const [isAuthenticatingTelegram, setIsAuthenticatingTelegram] = useState(() => authBypassEnabled ? false : isTelegramSession());
    const [isReady, setIsReady] = useState(() => authBypassEnabled || !isTelegramSession());
    const [hasTelegramWebApp, setHasTelegramWebApp] = useState(() => !authBypassEnabled && isTelegramSession() && Boolean(getTelegramWebApp()));

    useEffect(() => {
        if (authBypassEnabled) {
            setToken(AUTH_BYPASS_TOKEN);
            setTelegramInitData("");
            setIsTelegram(false);
            setHasTelegramWebApp(false);
            setIsAuthenticatingTelegram(false);
            setIsReady(true);
            return undefined;
        }

        let isCancelled = false;
        const nextIsTelegram = isTelegramSession();
        const nextInitData = getTelegramInitData();

        setIsTelegram(nextIsTelegram);
        setHasTelegramWebApp(nextIsTelegram && Boolean(getTelegramWebApp()));

        if (!nextIsTelegram) {
            setTelegramInitData("");
            setIsTelegram(false);
            setHasTelegramWebApp(false);
            setIsAuthenticatingTelegram(false);
            setIsReady(true);
            return undefined;
        }

        const authorizeTelegram = async (initData) => {
            if (!initData || isCancelled) {
                return;
            }

            setTelegramInitData(initData);
            setIsAuthenticatingTelegram(true);

            try {
                const response = await apiPost(
                    "/cabinet/auth/telegram-webapp",
                    {},
                    { telegramInitData: initData },
                );

                if (isCancelled) {
                    return;
                }

                if (response?.access_token) {
                    localStorage.setItem(TOKEN_STORAGE_KEY, response.access_token);
                    setToken(response.access_token);
                } else {
                    localStorage.removeItem(TOKEN_STORAGE_KEY);
                    setToken("");
                }
            } catch {
                if (isCancelled) {
                    return;
                }

                localStorage.removeItem(TOKEN_STORAGE_KEY);
                setToken("");
            } finally {
                if (!isCancelled) {
                    setIsAuthenticatingTelegram(false);
                    setIsReady(true);
                }
            }
        };

        if (nextInitData) {
            storeTelegramInitData(nextInitData);
            void authorizeTelegram(nextInitData);
        } else {
            setTelegramInitData("");
            localStorage.removeItem(TOKEN_STORAGE_KEY);
            setToken("");
            setIsAuthenticatingTelegram(false);
            setIsReady(true);
        }

        return () => {
            isCancelled = true;
        };
    }, [authBypassEnabled]);

    const login = useCallback((nextToken) => {
        localStorage.setItem(TOKEN_STORAGE_KEY, nextToken);
        setToken(nextToken);
        setIsReady(true);
    }, []);

    const logout = useCallback(() => {
        if (authBypassEnabled) {
            setToken(AUTH_BYPASS_TOKEN);
            setIsReady(true);
            return;
        }

        localStorage.removeItem(TOKEN_STORAGE_KEY);
        setToken("");
    }, [authBypassEnabled]);

    const value = useMemo(() => ({
        isReady,
        isTelegram,
        hasTelegramWebApp,
        isAuthenticatingTelegram,
        token,
        telegramInitData,
        isAuthBypass: authBypassEnabled,
        isAuthorized: authBypassEnabled || Boolean(token),
        login,
        logout,
    }), [authBypassEnabled, hasTelegramWebApp, isAuthenticatingTelegram, isReady, isTelegram, login, logout, telegramInitData, token]);

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);

    if (!context) {
        throw new Error("useAuth must be used inside AuthProvider");
    }

    return context;
}
